import { useEffect, useMemo, useState } from 'react';
import { canWriteEmpresaData } from '../../auth/session';
import { createItem, deleteItem, fetchList, fetchLookups, postAction, updateItem } from './api';
import CrudModal from '../../components/layout/CrudModal';
import { formatCurrencyBR, formatDecimalBR, normalizeCurrencyInputBR, normalizeDecimalInputBR, normalizeIntegerInputBR, parseCurrencyBR, parseDecimalBR, parseIntegerBR } from '../../utils/numberBR';

const EMPTY_CONTRATO = {
  obra_id: '',
  nome: '',
  contratante: '',
  valor_contrato: '',
  data_inicio: '',
  data_fim_prevista: '',
  status: 'ativo',
  cronograma_resumo: '',
  percentual_executado: '',
};

const EMPTY_MEDICAO = {
  etapa_nome: '',
  ordem: '1',
  percentual_previsto: '',
  percentual_medido: '',
  valor_previsto: '',
  valor_medido: '',
  data_medicao: '',
  status: 'pendente',
  observacoes: '',
};

const moeda = (v) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ConstrutorContratosPage = () => {
  const canWrite = canWriteEmpresaData();
  const [lookups, setLookups] = useState({ obras: [] });
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY_CONTRATO);
  const [editingId, setEditingId] = useState('');
  const [showContratoModal, setShowContratoModal] = useState(false);
  const [q, setQ] = useState('');
  const [obraFilter, setObraFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [selectedContratoId, setSelectedContratoId] = useState('');
  const [medicoes, setMedicoes] = useState([]);
  const [medicaoForm, setMedicaoForm] = useState(EMPTY_MEDICAO);
  const [editingMedicaoId, setEditingMedicaoId] = useState('');
  const [showMedicaoModal, setShowMedicaoModal] = useState(false);

  const loadContratos = async () => {
    setLoading(true);
    try {
      const [list, lk] = await Promise.all([
        fetchList('/api/construtor/contratos', { q, obra_id: obraFilter, page, pageSize: 10 }),
        fetchLookups(),
      ]);
      setItems(Array.isArray(list?.items) ? list.items : []);
      setTotal(Number(list?.total || 0));
      setLookups({ obras: lk?.obras || [] });
      const initialContrato = selectedContratoId || list?.items?.[0]?.id || '';
      if (initialContrato && !selectedContratoId) {
        setSelectedContratoId(initialContrato);
        const data = await fetchList(`/api/construtor/contratos/${initialContrato}/medicoes`);
        setMedicoes(Array.isArray(data?.items) ? data.items : []);
      } else if (selectedContratoId) {
        const data = await fetchList(`/api/construtor/contratos/${selectedContratoId}/medicoes`);
        setMedicoes(Array.isArray(data?.items) ? data.items : []);
      } else {
        setMedicoes([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadMedicoes = async (contratoId) => {
    if (!contratoId) {
      setMedicoes([]);
      return;
    }
    const data = await fetchList(`/api/construtor/contratos/${contratoId}/medicoes`);
    setMedicoes(Array.isArray(data?.items) ? data.items : []);
  };

  useEffect(() => {
    loadContratos();
  }, [page]);

  const onSubmitContrato = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      valor_contrato: parseCurrencyBR(form.valor_contrato) ?? 0,
      percentual_executado: parseDecimalBR(form.percentual_executado) ?? 0,
    };
    if (editingId) {
      await updateItem('/api/construtor/contratos', editingId, payload);
    } else {
      await createItem('/api/construtor/contratos', payload);
    }
    setEditingId('');
    setForm(EMPTY_CONTRATO);
    setShowContratoModal(false);
    await loadContratos();
  };

  const onEditContrato = (item) => {
    setEditingId(item.id);
    setForm({
      obra_id: item.obra_id || '',
      nome: item.nome || '',
      contratante: item.contratante || '',
      valor_contrato: item.valor_contrato == null ? '' : formatCurrencyBR(item.valor_contrato),
      data_inicio: String(item.data_inicio || '').slice(0, 10),
      data_fim_prevista: String(item.data_fim_prevista || '').slice(0, 10),
      status: item.status || 'ativo',
      cronograma_resumo: item.cronograma_resumo || '',
      percentual_executado: item.percentual_executado == null ? '' : formatDecimalBR(item.percentual_executado, 2),
    });
    setShowContratoModal(true);
  };

  const onDeleteContrato = async (id) => {
    if (!window.confirm('Excluir contrato e medicoes vinculadas?')) return;
    await deleteItem('/api/construtor/contratos', id);
    if (selectedContratoId === id) {
      setSelectedContratoId('');
      setMedicoes([]);
    }
    await loadContratos();
  };

  const onSubmitMedicao = async (e) => {
    e.preventDefault();
    if (!selectedContratoId) return;
    const payload = {
      ...medicaoForm,
      ordem: parseIntegerBR(medicaoForm.ordem) ?? 1,
      percentual_previsto: parseDecimalBR(medicaoForm.percentual_previsto) ?? 0,
      percentual_medido: parseDecimalBR(medicaoForm.percentual_medido) ?? 0,
      valor_previsto: parseCurrencyBR(medicaoForm.valor_previsto) ?? 0,
      valor_medido: parseCurrencyBR(medicaoForm.valor_medido) ?? 0,
      data_medicao: medicaoForm.data_medicao || null,
    };
    if (editingMedicaoId) {
      await updateItem('/api/construtor/medicoes', editingMedicaoId, payload);
    } else {
      await createItem(`/api/construtor/contratos/${selectedContratoId}/medicoes`, payload);
    }
    setEditingMedicaoId('');
    setMedicaoForm(EMPTY_MEDICAO);
    setShowMedicaoModal(false);
    await loadMedicoes(selectedContratoId);
    await loadContratos();
  };

  const onEditMedicao = (item) => {
    setEditingMedicaoId(item.id);
    setMedicaoForm({
      etapa_nome: item.etapa_nome || '',
      ordem: item.ordem == null ? '1' : String(item.ordem),
      percentual_previsto: item.percentual_previsto == null ? '' : formatDecimalBR(item.percentual_previsto, 2),
      percentual_medido: item.percentual_medido == null ? '' : formatDecimalBR(item.percentual_medido, 2),
      valor_previsto: item.valor_previsto == null ? '' : formatCurrencyBR(item.valor_previsto),
      valor_medido: item.valor_medido == null ? '' : formatCurrencyBR(item.valor_medido),
      data_medicao: String(item.data_medicao || '').slice(0, 10),
      status: item.status || 'pendente',
      observacoes: item.observacoes || '',
    });
    setShowMedicaoModal(true);
  };

  const onDeleteMedicao = async (id) => {
    if (!window.confirm('Excluir medicao?')) return;
    await deleteItem('/api/construtor/medicoes', id);
    await loadMedicoes(selectedContratoId);
    await loadContratos();
  };

  const onLiberarMedicao = async (id) => {
    await postAction(`/api/construtor/medicoes/${id}/liberar`, {});
    await loadMedicoes(selectedContratoId);
    await loadContratos();
  };

  const totalPages = Math.max(1, Math.ceil(total / 10));
  const resumoMedicoes = useMemo(() => {
    return medicoes.reduce(
      (acc, item) => {
        acc.total += Number(item.valor_medido || 0);
        if (item.status === 'liberada') acc.liberado += Number(item.valor_medido || 0);
        return acc;
      },
      { total: 0, liberado: 0 }
    );
  }, [medicoes]);

  return (
    <div className="animate-fade-in space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Contratos e Medicoes</h1>
        <p className="text-gray-600">Controle contratual, cronograma fisico-financeiro, medicoes por etapa e liberacao de recebimentos.</p>
      </div>
      {canWrite && (
        <div className="flex justify-end">
          <button
            type="button"
            className="btn-primary"
            onClick={() => {
              setEditingId('');
              setForm(EMPTY_CONTRATO);
              setShowContratoModal(true);
            }}
          >
            Incluir Contrato
          </button>
        </div>
      )}

      {canWrite && (
        <CrudModal
          open={showContratoModal}
          title={editingId ? 'Editar Contrato' : 'Novo Contrato'}
          onClose={() => {
            setShowContratoModal(false);
            setEditingId('');
            setForm(EMPTY_CONTRATO);
          }}
          maxWidthClass="max-w-6xl"
        >
          <form onSubmit={onSubmitContrato} className="space-y-5">
            <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-4">
              <p className="text-sm font-semibold text-gray-800">Dados Contratuais</p>
              <p className="text-xs text-gray-500 mt-1">Configure contrato e cronograma fisico-financeiro da obra.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="form-label">Obra</label>
                <select className="form-select" value={form.obra_id} onChange={(e) => setForm({ ...form, obra_id: e.target.value })}>
                  <option value="">Selecione</option>
                  {lookups.obras.map((obra) => <option key={obra.id} value={obra.id}>{obra.nome}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label">Nome do Contrato</label>
                <input className="form-input" placeholder="Ex.: Contrato Estrutura" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
              </div>
              <div>
                <label className="form-label">Contratante</label>
                <input className="form-input" placeholder="Empresa / Pessoa" value={form.contratante} onChange={(e) => setForm({ ...form, contratante: e.target.value })} />
              </div>
              <div>
                <label className="form-label">Valor do Contrato (R$)</label>
                <input
                  className="form-input"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={form.valor_contrato}
                  onChange={(e) => setForm({ ...form, valor_contrato: normalizeCurrencyInputBR(e.target.value) })}
                />
              </div>
              <div>
                <label className="form-label">Data de Inicio</label>
                <input className="form-input" type="date" value={form.data_inicio} onChange={(e) => setForm({ ...form, data_inicio: e.target.value })} />
              </div>
              <div>
                <label className="form-label">Data Fim Prevista</label>
                <input className="form-input" type="date" value={form.data_fim_prevista} onChange={(e) => setForm({ ...form, data_fim_prevista: e.target.value })} />
              </div>
              <div>
                <label className="form-label">Status</label>
                <select className="form-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  <option value="ativo">Ativo</option>
                  <option value="pausado">Pausado</option>
                  <option value="concluido">Concluido</option>
                  <option value="cancelado">Cancelado</option>
                </select>
              </div>
              <div>
                <label className="form-label">% Executado</label>
                <input
                  className="form-input"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={form.percentual_executado}
                  onChange={(e) => setForm({ ...form, percentual_executado: normalizeDecimalInputBR(e.target.value, 2) })}
                />
              </div>
              <div className="md:col-span-4">
                <label className="form-label">Cronograma Fisico-financeiro (resumo)</label>
                <textarea className="form-textarea" rows="2" placeholder="Resumo das etapas e valores planejados" value={form.cronograma_resumo} onChange={(e) => setForm({ ...form, cronograma_resumo: e.target.value })} />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t pt-4">
              <button
                className="btn-secondary"
                type="button"
                onClick={() => {
                  setShowContratoModal(false);
                  setEditingId('');
                  setForm(EMPTY_CONTRATO);
                }}
              >
                Cancelar
              </button>
              <button className="btn-primary" type="submit">{editingId ? 'Atualizar Contrato' : 'Salvar Contrato'}</button>
            </div>
          </form>
        </CrudModal>
      )}

      <div className="card">
        <div className="card-header flex items-center gap-3">
          <select className="form-select max-w-sm" value={obraFilter} onChange={(e) => setObraFilter(e.target.value)}>
            <option value="">Todas obras</option>
            {lookups.obras.map((obra) => <option key={obra.id} value={obra.id}>{obra.nome}</option>)}
          </select>
          <input className="form-input max-w-sm" placeholder="Buscar contrato/obra..." value={q} onChange={(e) => setQ(e.target.value)} />
          <button className="btn-secondary" onClick={() => { setPage(1); loadContratos(); }}>Filtrar</button>
        </div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Contrato</th>
                  <th>Obra</th>
                  <th>Status</th>
                  <th>Valor</th>
                  <th>Executado</th>
                  <th>Liberado</th>
                  <th>Acoes</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td>{item.nome}</td>
                    <td>{item.obra_nome}</td>
                    <td>{item.status}</td>
                    <td>{moeda(item.valor_contrato)}</td>
                    <td>{Number(item.percentual_executado || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%</td>
                    <td>{moeda(item.valor_liberado)}</td>
                    <td>
                      <button className="btn-secondary mr-2" onClick={() => { setSelectedContratoId(item.id); loadMedicoes(item.id); }}>Medicoes</button>
                      {canWrite && <button className="btn-secondary mr-2" onClick={() => onEditContrato(item)}>Editar</button>}
                      {canWrite && <button className="btn-danger" onClick={() => onDeleteContrato(item.id)}>Excluir</button>}
                    </td>
                  </tr>
                ))}
                {!loading && items.length === 0 && <tr><td colSpan={7} className="text-center text-gray-500 py-6">Sem contratos</td></tr>}
                {loading && <tr><td colSpan={7} className="text-center text-gray-500 py-6">Carregando...</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card-body border-t flex items-center justify-between">
          <span className="text-sm text-gray-600">Pagina {page} de {totalPages} ({total} registros)</span>
          <div className="flex gap-2">
            <button className="btn-secondary" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Anterior</button>
            <button className="btn-secondary" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Proxima</button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header flex items-center justify-between gap-3">
          <span>Medicoes por Etapa</span>
          {canWrite && (
            <button
              type="button"
              className="btn-primary"
              disabled={!selectedContratoId}
              onClick={() => {
                if (!selectedContratoId) return;
                setEditingMedicaoId('');
                setMedicaoForm(EMPTY_MEDICAO);
                setShowMedicaoModal(true);
              }}
            >
              Incluir Medicao
            </button>
          )}
        </div>
        <div className="card-body grid grid-cols-1 md:grid-cols-3 gap-3 border-b">
          <div>
            <p className="text-xs text-gray-500">Contrato selecionado</p>
            <p className="font-medium">{items.find((x) => x.id === selectedContratoId)?.nome || '-'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Valor medido total</p>
            <p className="font-medium">{moeda(resumoMedicoes.total)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Valor liberado</p>
            <p className="font-medium">{moeda(resumoMedicoes.liberado)}</p>
          </div>
        </div>

        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Etapa</th>
                  <th>Previsto</th>
                  <th>Medido</th>
                  <th>Status</th>
                  <th>Data</th>
                  <th>Acoes</th>
                </tr>
              </thead>
              <tbody>
                {medicoes.map((item) => (
                  <tr key={item.id}>
                    <td>{item.etapa_nome}</td>
                    <td>{moeda(item.valor_previsto)}</td>
                    <td>{moeda(item.valor_medido)}</td>
                    <td>{item.status}</td>
                    <td>{String(item.data_medicao || '').slice(0, 10) || '-'}</td>
                    <td>
                      {canWrite && <button className="btn-secondary mr-2" onClick={() => onEditMedicao(item)}>Editar</button>}
                      {canWrite && item.status !== 'liberada' && <button className="btn-secondary mr-2" onClick={() => onLiberarMedicao(item.id)}>Liberar</button>}
                      {canWrite && <button className="btn-danger" onClick={() => onDeleteMedicao(item.id)}>Excluir</button>}
                    </td>
                  </tr>
                ))}
                {medicoes.length === 0 && <tr><td colSpan={6} className="text-center text-gray-500 py-6">Sem medicoes para o contrato selecionado</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {canWrite && selectedContratoId && (
        <CrudModal
          open={showMedicaoModal}
          title={editingMedicaoId ? 'Editar Medicao' : 'Nova Medicao'}
          onClose={() => {
            setShowMedicaoModal(false);
            setEditingMedicaoId('');
            setMedicaoForm(EMPTY_MEDICAO);
          }}
          maxWidthClass="max-w-6xl"
        >
          <form className="space-y-5" onSubmit={onSubmitMedicao}>
            <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-4">
              <p className="text-sm font-semibold text-gray-800">Medicao por Etapa</p>
              <p className="text-xs text-gray-500 mt-1">Informe avance fisico-financeiro e status da medicao.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="form-label">Etapa</label>
                <input className="form-input" placeholder="Ex.: Fundacao" value={medicaoForm.etapa_nome} onChange={(e) => setMedicaoForm({ ...medicaoForm, etapa_nome: e.target.value })} />
              </div>
              <div>
                <label className="form-label">Ordem</label>
                <input
                  className="form-input"
                  inputMode="numeric"
                  placeholder="1"
                  value={medicaoForm.ordem}
                  onChange={(e) => setMedicaoForm({ ...medicaoForm, ordem: normalizeIntegerInputBR(e.target.value) })}
                />
              </div>
              <div>
                <label className="form-label">% Previsto</label>
                <input
                  className="form-input"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={medicaoForm.percentual_previsto}
                  onChange={(e) => setMedicaoForm({ ...medicaoForm, percentual_previsto: normalizeDecimalInputBR(e.target.value, 2) })}
                />
              </div>
              <div>
                <label className="form-label">% Medido</label>
                <input
                  className="form-input"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={medicaoForm.percentual_medido}
                  onChange={(e) => setMedicaoForm({ ...medicaoForm, percentual_medido: normalizeDecimalInputBR(e.target.value, 2) })}
                />
              </div>
              <div>
                <label className="form-label">Valor Previsto (R$)</label>
                <input
                  className="form-input"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={medicaoForm.valor_previsto}
                  onChange={(e) => setMedicaoForm({ ...medicaoForm, valor_previsto: normalizeCurrencyInputBR(e.target.value) })}
                />
              </div>
              <div>
                <label className="form-label">Valor Medido (R$)</label>
                <input
                  className="form-input"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={medicaoForm.valor_medido}
                  onChange={(e) => setMedicaoForm({ ...medicaoForm, valor_medido: normalizeCurrencyInputBR(e.target.value) })}
                />
              </div>
              <div>
                <label className="form-label">Data da Medicao</label>
                <input className="form-input" type="date" value={medicaoForm.data_medicao} onChange={(e) => setMedicaoForm({ ...medicaoForm, data_medicao: e.target.value })} />
              </div>
              <div>
                <label className="form-label">Status</label>
                <select className="form-select" value={medicaoForm.status} onChange={(e) => setMedicaoForm({ ...medicaoForm, status: e.target.value })}>
                  <option value="pendente">Pendente</option>
                  <option value="aprovada">Aprovada</option>
                  <option value="reprovada">Reprovada</option>
                  <option value="liberada">Liberada</option>
                </select>
              </div>
              <div className="md:col-span-4">
                <label className="form-label">Observacoes</label>
                <textarea className="form-textarea" rows="2" placeholder="Comentarios da medicao" value={medicaoForm.observacoes} onChange={(e) => setMedicaoForm({ ...medicaoForm, observacoes: e.target.value })} />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t pt-4">
              <button
                className="btn-secondary"
                type="button"
                onClick={() => {
                  setShowMedicaoModal(false);
                  setEditingMedicaoId('');
                  setMedicaoForm(EMPTY_MEDICAO);
                }}
              >
                Cancelar
              </button>
              <button className="btn-primary" type="submit">{editingMedicaoId ? 'Atualizar Medicao' : 'Salvar Medicao'}</button>
            </div>
          </form>
        </CrudModal>
      )}
    </div>
  );
};

export default ConstrutorContratosPage;
